import { Text } from '../../primitives/Text';
import { cn } from '../../utils/cn';
import { shade700 } from '../../utils/color';

/**
 * Is this label a single EMOJI rather than a text tag? Code points, not
 * `.length`: an emoji carrying a variation selector ('⚡️', '🏎️') is two code
 * points but still one glyph, while a 2-letter tag ("PE") is short enough to
 * render at glyph size anyway.
 */
export const isGlyphLabel = (label: string): boolean => [...label].length <= 2;

/**
 * A deck's "emoji / tag" — the free-text thumbnail label the apps cap at 8
 * characters, so it holds either a single emoji (🏎️) or a short tag
 * ("Chem 101").
 *
 * ONE definition for every surface a deck's art shows up on: DeckCard's grid
 * tile, the folder-stack pages inside FolderCard / StudyGroupCard, and the
 * list rows' preview thumbs — a deck's tag has to read the same in its own
 * card, in the folder that holds it, and in the dialogs that preview them.
 *
 * A TAG is text, so it takes the deck colour's -700 shade + Bold (Figma D2)
 * over the surface's 30%-white tint, and steps down to `tagSize` because the
 * folder pages (130–202px) and list thumbs (55px) are far narrower than a
 * deck tile — at glyph size an 8-character tag was cut mid-letter. A single
 * EMOJI keeps `glyphSize` (glyphs ignore `color`, and one glyph always fits).
 */
export function CardTagLabel({
  label,
  color,
  glyphSize,
  tagSize,
  tagLines = 1,
}: {
  /** The deck's `thumbnailText`. Empty / undefined renders nothing. */
  label?: string;
  /** Deck identity colour (hex) — the tag text takes its -700 shade. */
  color?: string;
  /** Font size for a single-emoji label, in px. */
  glyphSize: number;
  /** Font size for a multi-character tag, in px. */
  tagSize: number;
  /** How many lines a tag may wrap to before it truncates. Default 1. */
  tagLines?: number;
}) {
  if (!label) return null;
  const isGlyph = isGlyphLabel(label);
  const fontSize = isGlyph ? glyphSize : tagSize;
  const lineHeight = Math.round(fontSize * 1.22);
  const tint = isGlyph || !color ? undefined : { color: shade700(color) };
  // `lineHeight` is one of React's UNITLESS style props on web (a bare 34
  // there means 34× the font size), while RN wants exactly that bare number
  // — hence the two styles instead of one.
  const webStyle = { fontSize, lineHeight: `${lineHeight}px`, ...tint };
  const nativeStyle = { fontSize, lineHeight, ...tint };
  return (
    <Text
      className={cn(
        'max-w-full text-center font-bold native:font-mona-bold',
        // Break at the space first, and mid-word only when a single word
        // ("WWWWWWWW") would overflow — native RN breaks such a word by
        // character on its own.
        !isGlyph && 'web:break-words',
      )}
      numberOfLines={isGlyph ? 1 : tagLines}
      webProps={{ style: webStyle }}
      nativeProps={{ style: nativeStyle }}
      selectable={false}>
      {label}
    </Text>
  );
}
(CardTagLabel as { displayName?: string }).displayName = 'CardTagLabel';
